import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { supabase, type User } from '../../lib/supabaseClient';
import toast from 'react-hot-toast';

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  category: 'veg' | 'non-veg';
  image_url: string;
}

interface FormData {
  name: string;
  description: string;
  price: string;
  category: 'veg' | 'non-veg';
  image_url: string;
}

const StyledWrapper = styled.div`
  .form-box {
    padding: 24px;
    background: #fff;
    box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
    border-radius: 12px;
  }

  .user-box {
    position: relative;
    margin-bottom: 20px;
  }

  .user-box input,
  .user-box textarea,
  .user-box select,
  .user-box input[type="file"] {
    width: 100%;
    padding: 12px 16px;
    font-size: 14px;
    color: #111827;
    border: 2px solid #d1d5db;
    border-radius: 8px;
    background: #f9fafb;
    transition: border-color 0.3s ease, box-shadow 0.3s ease;
  }

  .user-box input:focus,
  .user-box textarea:focus,
  .user-box select:focus,
  .user-box input[type="file"]:focus {
    border-color: #059669;
    box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.2);
    outline: none;
  }

  .user-box textarea {
    resize: vertical;
    min-height: 80px;
  }

  .user-box select {
    appearance: none;
    background: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iNyIgdmlld0JveD0iMCAwIDEyIDciIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTEgMS41TDYgNS41TDExIDEuNSIgc3Ryb2tlPSIjMDU5NjY5IiBzdHJva2Utd2lkdGg9IjIiLz48L3N2Zz4=') no-repeat right 16px center;
    background-size: 12px;
  }

  .user-box input[type="file"] {
    padding: 10px 16px;
  }

  .user-box label {
    position: absolute;
    top: -8px;
    left: 12px;
    padding: 0 6px;
    font-size: 12px;
    color: #6b7280;
    background: #fff;
    transition: color 0.3s ease;
    pointer-events: none;
  }

  .user-box input:focus ~ label,
  .user-box textarea:focus ~ label,
  .user-box select:focus ~ label,
  .user-box input[type="file"]:focus ~ label {
    color: #059669;
  }

  .action-buttons {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 24px;
  }

  button {
    position: relative;
    padding: 10px 16px;
    font-size: 14px;
    color: #fff;
    background: #059669;
    border: 2px solid #059669;
    border-radius: 9999px;
    overflow: hidden;
    transition: all 0.3s ease;
    cursor: pointer;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    font-weight: 500;
  }

  button::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: -100%;
    background: #047857;
    transition: all 0.7s ease;
    border-radius: 9999px;
    z-index: -1;
  }

  button:hover::before {
    left: 0;
    transform: scale(1.5);
  }

  .cancel-btn {
    background: #6b7280;
    border-color: #6b7280;
  }

  .cancel-btn::before {
    background: #4b5563;
  }

  .dark .form-box { background: #1f2937; }
  .dark .user-box input,
  .dark .user-box textarea,
  .dark .user-box select,
  .dark .user-box input[type="file"] {
    color: #f3f4f6;
    background: #374151;
    border-color: #4b5563;
  }
  .dark .user-box label { color: #9ca3af; background: #1f2937; }
  .dark .user-box input:focus,
  .dark .user-box textarea:focus,
  .dark .user-box select:focus,
  .dark .user-box input[type="file"]:focus {
    border-color: #059669;
    box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.2);
  }
  .dark .user-box input:focus ~ label,
  .dark .user-box textarea:focus ~ label,
  .dark .user-box select:focus ~ label,
  .dark .user-box input[type="file"]:focus ~ label {
    color: #059669;
  }
  .dark button { color: #f3f4f6; background: #059669; border-color: #059669; }
  .dark button::before { background: #047857; }
  .dark .cancel-btn { background: #6b7280; border-color: #6b7280; }
  .dark .cancel-btn::before { background: #4b5563; }
`;

const MenuManagement: React.FC = () => {
  const [user, setUser] = useState<User | null>(null); // Store authenticated user
  const [authLoading, setAuthLoading] = useState(true); // Loading state for auth
  const [items, setItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    price: '',
    category: 'veg',
    image_url: ''
  });

  const defaultFormData: FormData = {
    name: '',
    description: '',
    price: '',
    category: 'veg',
    image_url: ''
  };

  // Check and restore Supabase session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        setAuthLoading(true);
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        setUser(session?.user ?? null);
      } catch (error) {
        console.error('Error fetching session:', error);
        // Redirect to sign-in if session check fails
        window.location.href = '/signin';
      } finally {
        setAuthLoading(false);
      }
    };

    checkSession();

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });

    // Cleanup listener on unmount
    return () => subscription.unsubscribe();
  }, []);

  // Restore form state and fetch menu items when user is authenticated
  useEffect(() => {
    if (user) {
      const savedState = {
        isModalOpen: localStorage.getItem('isModalOpen') === 'true',
        formData: JSON.parse(localStorage.getItem('formData') || 'null'),
        editingItem: JSON.parse(localStorage.getItem('editingItem') || 'null')
      };

      setIsModalOpen(savedState.isModalOpen);
      if (savedState.formData) setFormData(savedState.formData);
      if (savedState.editingItem) setEditingItem(savedState.editingItem);

      fetchMenuItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Persist form state to localStorage
  useEffect(() => {
    localStorage.setItem('isModalOpen', String(isModalOpen));
    localStorage.setItem('formData', JSON.stringify(formData));
    localStorage.setItem('editingItem', JSON.stringify(editingItem));
  }, [isModalOpen, formData, editingItem]);

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.from('menu_items').select('*').order('name');
      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error('Error fetching menu items:', error);
      toast.error('Failed to fetch menu items');
    } finally {
      setLoading(false);
    }
  };

  const checkWebPSupport = (): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = 'data:image/webp;base64,UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA';
    });
  };

  const convertToWebP = (file: File, maxWidth: number = 400, maxHeight: number = 400): Promise<File> => {
    return new Promise(async (resolve, reject) => {
      console.log('Starting WebP conversion for file:', file.name, file.type);

      const supportsWebP = await checkWebPSupport();
      if (!supportsWebP) {
        console.error('Browser does not support WebP encoding');
        reject(new Error('Browser does not support WebP encoding'));
        return;
      }

      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        console.error('Canvas context not supported');
        reject(new Error('Canvas context not supported'));
        return;
      }

      img.onload = () => {
        console.log('Image loaded successfully. Original dimensions:', img.width, 'x', img.height);
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }
        canvas.width = width;
        canvas.height = height;
        console.log('Resized dimensions:', width, 'x', height);

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              console.log('WebP conversion successful. Blob size:', blob.size);
              const webpFile = new File([blob], `${Date.now()}.webp`, { type: 'image/webp' });
              resolve(webpFile);
            } else {
              console.error('Failed to convert image to WebP');
              reject(new Error('Failed to convert image to WebP'));
            }
          },
          'image/webp',
          1
        );
      };

      img.onerror = () => {
        console.error('Failed to load image for conversion');
        reject(new Error('Failed to load image'));
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      console.log('Uploading image:', file.name, 'Size:', file.size, 'Type:', file.type);
      const webpFile = await convertToWebP(file, 400, 400);
      console.log('Converted to WebP:', webpFile.name, 'Size:', webpFile.size);

      const uniqueFileName = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.webp`;
      const filePath = `menuimages/${uniqueFileName}`;
      console.log('Uploading to path:', filePath);

      const { data, error } = await supabase.storage.from('menuimages').upload(filePath, webpFile, {
        contentType: 'image/webp',
        cacheControl: '31536000',
        upsert: false
      });

      if (error) {
        console.error('Supabase upload error details:', JSON.stringify(error, null, 2));
        throw new Error(`Upload failed: ${error.message}`);
      }

      console.log('Upload response data:', JSON.stringify(data, null, 2));
      const { data: urlData } = supabase.storage.from('menuimages').getPublicUrl(filePath);
      const publicUrl = urlData.publicUrl;
      console.log('Image uploaded successfully. Public URL:', publicUrl);
      return publicUrl;
    } catch (error) {
      console.error('Image upload failed with error:', JSON.stringify(error, null, 2));
      toast.error('Image upload failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
      return null;
    }
  };

  const resetForm = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setImageFile(null);
    setFormData(defaultFormData);
    // Remove only specific localStorage keys to preserve auth tokens
    localStorage.removeItem('isModalOpen');
    localStorage.removeItem('formData');
    localStorage.removeItem('editingItem');
  };

  const openModal = (item?: MenuItem) => {
    setEditingItem(item || null);
    setFormData(item ? { ...item, price: item.price.toString() } : defaultFormData);
    setImageFile(null);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const imageUrl = imageFile ? await uploadImage(imageFile) : formData.image_url;
      if (imageFile && !imageUrl) throw new Error('Image upload failed');

      const menuItem: Omit<MenuItem, 'id'> = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        category: formData.category,
        image_url: imageUrl || ''
      };

      const { error } = editingItem
        ? await supabase.from('menu_items').update(menuItem).eq('id', editingItem.id)
        : await supabase.from('menu_items').insert([menuItem]);

      if (error) throw error;

      toast.success(`Menu item ${editingItem ? 'updated' : 'added'} successfully`);
      resetForm();
      fetchMenuItems();
    } catch (error) {
      console.error('Error saving menu item:', error);
      toast.error(editingItem ? 'Failed to update menu item' : 'Failed to add menu item');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const { error } = await supabase.from('menu_items').delete().eq('id', id);
      if (error) throw error;
      setItems(items.filter(item => item.id !== id));
      toast.success('Menu item deleted successfully');
    } catch (error) {
      console.error('Error deleting menu item:', error);
      toast.error('Failed to delete menu item');
    }
  };

  const LoadingSkeleton = () => (
    <section className="py-10 sm:py-20 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8 text-center">Menu Management</h2>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {Array(8).fill(null).map((_, i) => (
            <div key={i} className="animate-pulse bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="h-24 sm:h-48 bg-gray-300 dark:bg-gray-700" />
              <div className="p-2 sm:p-4">
                <div className="h-3 sm:h-4 bg-gray-300 dark:bg-gray-700 rounded w-3/4 mb-1 sm:mb-2" />
                <div className="h-3 sm:h-4 bg-gray-300 dark:bg-gray-700 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const MenuItemCard = ({ item }: { item: MenuItem }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden transform transition-all hover:scale-105">
      <div className="flex flex-col">
        <img
          src={item.image_url}
          alt={item.name}
          className="w-full h-24 sm:h-48 object-cover rounded-t-md"
          loading="lazy"
        />
        <div className="p-2 sm:p-4 flex flex-col justify-between">
          <div className="flex flex-col sm:mb-2 gap-1">
            <div className="flex items-center gap-1 sm:gap-2">
              <img
                src={item.category === 'veg' ? 'https://freesvg.org/img/1531813273.png' : 'https://freesvg.org/img/1531813245.png'}
                alt={item.category}
                className="w-3 h-3 sm:w-6 sm:h-6"
              />
              <h3 className="text-xs sm:text-lg font-semibold text-gray-900 dark:text-white truncate">{item.name}</h3>
            </div>
            <span className="text-emerald-600 font-semibold text-xs sm:text-base">£{item.price.toFixed(2)}</span>
          </div>
          <div className="flex flex-col">
            <p className="text-gray-600 dark:text-gray-400 text-[10px] sm:text-sm mb-1 sm:mb-4 line-clamp-2">{item.description}</p>
            <div className="flex items-center justify-between gap-1 sm:gap-2">
              <button onClick={() => openModal(item)} className="p-1 sm:p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700 rounded-full transition-colors">
                <Pencil className="w-3 h-3 sm:w-5 sm:h-5" />
              </button>
              <button onClick={() => handleDelete(item.id)} className="p-1 sm:p-2 text-red-600 hover:bg-red-50 dark:hover:bg-gray-700 rounded-full transition-colors">
                <Trash2 className="w-3 h-3 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render auth loading state
  if (authLoading) {
    return <div className="flex items-center justify-center h-screen">Loading authentication...</div>;
  }

  // Redirect to sign-in if not authenticated
  if (!user) {
    window.location.href = '/signin';
    return null;
  }

  // Render main content
  return (
    <section className="py-10 sm:py-20 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 sm:mb-8 gap-4">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white text-center sm:text-left">Menu Management</h2>
          <button onClick={() => openModal()} className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
            <Plus className="w-5 h-5" />
            <span>Add Item</span>
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {items.map(item => <MenuItemCard key={item.id} item={item} />)}
        </div>

        {isModalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 sm:p-4 sm:items-center z-50 overflow-y-auto"
            onClick={(e) => e.target === e.currentTarget && window.innerWidth >= 640 && resetForm()}
          >
            <div className="w-full max-w-md sm:max-h-[90vh] overflow-y-auto">
              <StyledWrapper>
                <div className="form-box">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">{editingItem ? 'Edit' : 'Add'} Menu Item</h3>
                  <form onSubmit={handleSubmit}>
                    <div className="user-box">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        autoComplete="off"
                      />
                      <label>Item Name</label>
                    </div>
                    <div className="user-box">
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        required
                      />
                      <label>Description</label>
                    </div>
                    <div className="user-box">
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        required
                        step="0.01"
                      />
                      <label>Price</label>
                    </div>
                    <div className="user-box">
                      <select
                        name="category"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value as 'veg' | 'non-veg' })}
                      >
                        <option value="veg">Veg</option>
                        <option value="non-veg">Non-Veg</option>
                      </select>
                      <label>Category</label>
                    </div>
                    <div className="user-box">
                      <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
                      <label>Image</label>
                    </div>
                    <div className="action-buttons">
                      <button type="submit" disabled={loading}>
                        {loading ? 'Saving...' : editingItem ? 'Update' : 'Add'} Item
                      </button>
                      <button type="button" className="cancel-btn" onClick={resetForm}>
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </StyledWrapper>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default MenuManagement;